import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SavedPromptCard from './components/SavedPromptCard';
import SearchFilterBar from './components/SearchFilterBar';
import BulkActionBar from './components/BulkActionBar';
import EmptyState from './components/EmptyState';
import EditPromptModal from './components/EditPromptModal';

const SavedPrompts = () => {
  const [prompts, setPrompts] = useState([]);
  const [filteredPrompts, setFilteredPrompts] = useState([]);
  const [selectedPrompts, setSelectedPrompts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [dateRange, setDateRange] = useState('all');
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [copySuccess, setCopySuccess] = useState('');

  // Mock data for saved prompts
  const mockPrompts = [
    {
      id: 1,
      content: `Create a comprehensive marketing campaign for a new eco-friendly water bottle targeting millennials and Gen Z consumers. Include social media strategy, influencer partnerships, and sustainability messaging that resonates with environmentally conscious buyers.`,
      category: 'marketing',
      tags: ['social-media', 'sustainability', 'millennials'],
      createdAt: '2025-01-04T10:30:00Z',
      updatedAt: '2025-01-04T10:30:00Z'
    },
    {
      id: 2,
      content: `Draft a privacy policy template for a SaaS application that collects user data, includes GDPR compliance requirements, and covers data retention, user rights, and third-party integrations.`,
      category: 'legal',
      tags: ['privacy', 'gdpr', 'saas'],
      createdAt: '2025-01-03T14:15:00Z',
      updatedAt: '2025-01-03T14:15:00Z'
    },
    {
      id: 3,
      content: `Write a technical documentation guide for implementing OAuth 2.0 authentication in a React application, including code examples, security best practices, and error handling strategies.`,
      category: 'technical',
      tags: ['oauth', 'react', 'authentication'],
      createdAt: '2025-01-02T09:45:00Z',
      updatedAt: '2025-01-02T09:45:00Z'
    },
    {
      id: 4,
      content: `Generate creative copy for a luxury watch brand's holiday campaign, emphasizing craftsmanship, heritage, and the perfect gift for special occasions. Include headlines, body copy, and call-to-action variations.`,
      category: 'creative',
      tags: ['luxury', 'holiday', 'copywriting'],
      createdAt: '2025-01-01T16:20:00Z',updatedAt: '2025-01-01T16:20:00Z'
    },
    {
      id: 5,
      content: `Create a business plan outline for a food delivery startup focusing on healthy meal options, including market analysis, revenue model, operational strategy, and funding requirements.`,
      category: 'business',
      tags: ['startup', 'food-delivery', 'business-plan'],
      createdAt: '2024-12-30T11:10:00Z',updatedAt: '2024-12-30T11:10:00Z'
    },
    {
      id: 6,
      content: `Design an email marketing sequence for onboarding new customers to a project management tool, including welcome emails, feature tutorials, and engagement strategies to reduce churn.`,
      category: 'marketing',
      tags: ['email-marketing', 'onboarding', 'saas'],
      createdAt: '2024-12-29T13:30:00Z',updatedAt: '2024-12-29T13:30:00Z'
    }
  ];

  useEffect(() => {
    // Simulate loading saved prompts
    const loadPrompts = async () => {
      setIsLoading(true);
      await new Promise(resolve => setTimeout(resolve, 1000));
      setPrompts(mockPrompts);
      setIsLoading(false);
    };

    loadPrompts();
  }, []);

  useEffect(() => {
    // Filter and sort prompts
    let filtered = [...prompts];

    // Apply search filter
    if (searchQuery) {
      filtered = filtered?.filter(prompt =>
        prompt?.content?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        prompt?.tags?.some(tag => tag?.toLowerCase()?.includes(searchQuery?.toLowerCase()))
      );
    }

    // Apply category filter
    if (selectedCategory !== 'all') {
      filtered = filtered?.filter(prompt => prompt?.category === selectedCategory);
    }

    // Apply date range filter
    if (dateRange !== 'all') {
      const now = new Date();
      const filterDate = new Date();
      
      switch (dateRange) {
        case 'today':
          filterDate?.setHours(0, 0, 0, 0);
          break;
        case 'week':
          filterDate?.setDate(now?.getDate() - 7);
          break;
        case 'month':
          filterDate?.setMonth(now?.getMonth() - 1);
          break;
        case 'quarter':
          filterDate?.setMonth(now?.getMonth() - 3);
          break;
        default:
          break;
      }
      
      if (dateRange !== 'all') {
        filtered = filtered?.filter(prompt => new Date(prompt.createdAt) >= filterDate);
      }
    }

    // Apply sorting
    filtered?.sort((a, b) => {
      switch (sortBy) {
        case 'oldest':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'category':
          return a?.category?.localeCompare(b?.category);
        case 'alphabetical':
          return a?.content?.localeCompare(b?.content);
        case 'newest':
        default:
          return new Date(b.createdAt) - new Date(a.createdAt);
      }
    });

    setFilteredPrompts(filtered);
  }, [prompts, searchQuery, selectedCategory, sortBy, dateRange]);

  const handleCopy = (promptId) => {
    setCopySuccess(promptId);
    setTimeout(() => setCopySuccess(''), 2000);
  };

  const handleEdit = (prompt) => {
    setEditingPrompt(prompt);
  };

  const handleSaveEdit = (updatedPrompt) => {
    setPrompts(prev => prev?.map(p => p?.id === updatedPrompt?.id ? updatedPrompt : p));
    setEditingPrompt(null);
  };

  const handleDelete = (promptId) => {
    setPrompts(prev => prev?.filter(p => p?.id !== promptId));
    setSelectedPrompts(prev => prev?.filter(id => id !== promptId));
  };

  const handleSelectPrompt = (promptId) => {
    setSelectedPrompts(prev =>
      prev?.includes(promptId)
        ? prev?.filter(id => id !== promptId)
        : [...prev, promptId]
    );
  };

  const handleSelectAll = () => {
    setSelectedPrompts(filteredPrompts?.map(p => p?.id));
  };

  const handleDeselectAll = () => {
    setSelectedPrompts([]);
  };

  const handleBulkDelete = () => {
    setPrompts(prev => prev?.filter(p => !selectedPrompts?.includes(p?.id)));
    setSelectedPrompts([]);
  };

  const handleBulkExport = () => {
    const selectedPromptsData = prompts?.filter(p => selectedPrompts?.includes(p?.id));
    const exportData = JSON.stringify(selectedPromptsData, null, 2);
    const blob = new Blob([exportData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `saved-prompts-${new Date()?.toISOString()?.split('T')?.[0]}.json`;
    document.body?.appendChild(a);
    a?.click();
    document.body?.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleBulkCategorize = (newCategory) => {
    setPrompts(prev => prev?.map(p =>
      selectedPrompts?.includes(p?.id)
        ? { ...p, category: newCategory, updatedAt: new Date()?.toISOString() }
        : p
    ));
    setSelectedPrompts([]);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
    setDateRange('all');
    setSortBy('newest');
  };

  const hasActiveFilters = searchQuery || selectedCategory !== 'all' || dateRange !== 'all';

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16">
          <div className="max-w-7xl mx-auto px-6 py-8">
            <div className="flex items-center justify-center py-16">
              <div className="text-center space-y-4">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto animate-pulse">
                  <Icon name="BookOpen" size={24} color="var(--color-primary)" />
                </div>
                <p className="text-text-secondary">Loading your saved prompts...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-text-primary mb-2">
                  Saved Prompts
                </h1>
                <p className="text-text-secondary">
                  Manage and organize your collection of AI-generated prompts
                </p>
              </div>
              <Link to="/prompt-generator">
                <Button variant="default" iconName="Plus" iconSize={16}>
                  Generate New Prompt
                </Button>
              </Link>
            </div>
          </div>

          {prompts?.length === 0 ? (
            <EmptyState hasFilters={false} onClearFilters={handleClearFilters} />
          ) : (
            <>
              {/* Search and Filter Bar */}
              <SearchFilterBar
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                selectedCategory={selectedCategory}
                onCategoryChange={setSelectedCategory}
                sortBy={sortBy}
                onSortChange={setSortBy}
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
                onClearFilters={handleClearFilters}
                totalResults={filteredPrompts?.length}
              />

              {/* Bulk Action Bar */}
              <BulkActionBar
                selectedCount={selectedPrompts?.length}
                totalCount={filteredPrompts?.length}
                onSelectAll={handleSelectAll}
                onDeselectAll={handleDeselectAll}
                onBulkDelete={handleBulkDelete}
                onBulkExport={handleBulkExport}
                onBulkCategorize={handleBulkCategorize}
              />

              {/* Prompts Grid */}
              {filteredPrompts?.length === 0 ? (
                <EmptyState hasFilters={hasActiveFilters} onClearFilters={handleClearFilters} />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredPrompts?.map((prompt) => (
                    <SavedPromptCard
                      key={prompt?.id}
                      prompt={prompt}
                      onCopy={handleCopy}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      isSelected={selectedPrompts?.includes(prompt?.id)}
                      onSelect={handleSelectPrompt}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {/* Copy Success Message */}
          {copySuccess && (
            <div className="fixed bottom-4 right-4 bg-success text-success-foreground px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2 z-50">
              <Icon name="Check" size={16} />
              <span>Prompt copied to clipboard!</span>
            </div>
          )}

          {/* Edit Prompt Modal */}
          <EditPromptModal
            prompt={editingPrompt}
            isOpen={!!editingPrompt}
            onClose={() => setEditingPrompt(null)}
            onSave={handleSaveEdit}
          />
        </div>
      </div>
    </div>
  );
};

export default SavedPrompts;